import {
  type EventPhoto,
  type NewGetRequest,
  type NewUpdateWithoutTokenRequest,
  NewUpdateWithTokenRequest,
  NULL_TIME,
  type Proposal,
  type QuoteLine,
  TokenProposal,
  URLObject,
} from '@kalos/kalos-rpc';
import { CustomerProposal, CustomerProposalComplete, type EvidencePhoto } from 'kalos-modules';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useKalosLogoQuery } from '../../../../hooks/react-query/useKalosLogoQuery';
import {
  useProposalGetWithTokenQuery,
  useProposalNewUpdateWithTokenMutation,
} from '../../../../hooks/react-query/useProposalQuery';
import { useQuoteDocumentQuery } from '../../../../hooks/react-query/useQuoteDocumentQuery';
import { ProposalClientService, S3ClientService } from '../../../../tools/helpers';
import { getQuoteDocumentTaxRate } from '../../../../utils/quoteDocumentPricing';
import { persistServiceNeededFromSignedQuoteLines } from '../../../../utils/signedProposalServiceDescription';

interface CustomerProposalWrapperProps {
  quoteDocumentId: number;
  reload?: () => void;
}

// EventPhoto carries fileBucket + fileName but no signed URL; resolve each so
// the proposal review can render the supporting-evidence photos by system.
const resolveEvidencePhotos = async (photos: EventPhoto[]): Promise<EvidencePhoto[]> => {
  const items = await Promise.all(
    photos
      .filter((p) => !!p.fileName)
      .map(async (p) => {
        try {
          const res = await S3ClientService.GetDownloadURL(
            URLObject.create({ bucket: p.fileBucket || 'testbuckethelios', key: p.fileName }),
          );
          return {
            id: p.id,
            src: res?.url || '',
            serviceItemId: p.serviceItemId,
            serviceItemName: p.serviceItemName,
          };
        } catch {
          return {
            id: p.id,
            src: '',
            serviceItemId: p.serviceItemId,
            serviceItemName: p.serviceItemName,
          };
        }
      }),
  );
  return items.filter((i) => i.src);
};

/** For callers that only have the event (e.g. ServiceCall tab): resolve primary quote doc and render by quoteDocumentId. */
export const CustomerProposalWrapperByEvent: FC<{
  serviceItem: { id: number };
  reload?: () => void;
}> = ({ serviceItem, reload }) => {
  const { data: quoteDocListData, isLoading } = useQuoteDocumentQuery({
    filters: { eventId: serviceItem.id, withoutLimit: true },
    enabled: !!serviceItem.id,
  });
  const primaryQuoteDoc = useMemo(() => {
    const list = quoteDocListData?.results || [];
    const primary = list.find((d) => !d.isFollowUpJob);
    return primary ?? list[0] ?? null;
  }, [quoteDocListData?.results]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500" />
          <p className="mt-4 text-gray-600">Loading proposal...</p>
        </div>
      </div>
    );
  }
  if (!primaryQuoteDoc?.id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="mt-4 text-gray-600">No proposal found for this job.</p>
        </div>
      </div>
    );
  }
  return <CustomerProposalWrapper quoteDocumentId={primaryQuoteDoc.id} reload={reload} />;
};

/** Token-based customer proposal (like public). For testing and email links: /customer-proposal/{token}. */
export const CustomerProposalWrapperToken: FC<{ token: string; reload?: () => void }> = ({
  token,
}) => {
  const kalosLogo = useKalosLogoQuery();
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: proposal, isLoading: loading } = useProposalGetWithTokenQuery({
    filter: { value: token },
    enabled: !!token,
  });
  const [evidencePhotos, setEvidencePhotos] = useState<EvidencePhoto[]>([]);
  useEffect(() => {
    let cancelled = false;
    resolveEvidencePhotos(proposal?.photos ?? []).then((items) => {
      if (!cancelled) setEvidencePhotos(items);
    });
    return () => {
      cancelled = true;
    };
  }, [proposal?.photos]);
  const { mutateAsync: updateWithToken, isPending: isSubmitting } =
    useProposalNewUpdateWithTokenMutation();
  const isProposalSigned = useMemo(() => {
    if (!proposal?.quoteDocument?.signedDate) return false;
    return (
      proposal.quoteDocument.signedDate !== NULL_TIME &&
      proposal.quoteDocument.signedDate !== '0000-00-00 00:00:00' &&
      proposal.quoteDocument.signedDate !== ''
    );
  }, [proposal?.quoteDocument?.signedDate]);
  const generateApprovedProposalUrl = useCallback(async () => {
    if (!proposal?.user?.id || (!proposal?.event?.id && !proposal?.quoteDocument?.id)) return '';

    const tryKey = async (key: string) => {
      try {
        const urlObj = URLObject.create({ bucket: 'testbuckethelios', key });
        const res = await S3ClientService.GetDownloadURL(urlObj);
        return res?.url || '';
      } catch {
        return '';
      }
    };

    let url = '';
    if (proposal.quoteDocument?.id) {
      url = await tryKey(`${proposal.quoteDocument.id}_approved_proposal_${proposal.user.id}.pdf`);
    }
    if (!url && proposal.event?.id) {
      url = await tryKey(`${proposal.event.id}_approved_proposal_${proposal.user.id}.pdf`);
    }
    return url;
  }, [proposal?.event?.id, proposal?.quoteDocument?.id, proposal?.user?.id]);
  useEffect(() => {
    if (isProposalSigned) generateApprovedProposalUrl().then(setDownloadUrl);
  }, [isProposalSigned, generateApprovedProposalUrl]);
  const handleComplete = useCallback(
    async (data: {
      acceptedServices: number[];
      signature: string;
      proposedBy: string;
      acceptedBy: string;
      signatureDate: string;
    }) => {
      if (!proposal || !token || isSubmitting) return;
      setError(null);
      try {
        const signatureBlob = b64toBlob(data.signature, 'image/png');
        const signatureArrayBuffer = await signatureBlob.arrayBuffer();
        const signatureData = new Uint8Array(signatureArrayBuffer);
        const updatedQuoteLines = proposal.quoteLines.map((quoteLine: QuoteLine) => ({
          ...quoteLine,
          quoteStatus: data.acceptedServices.includes(quoteLine.id) ? 1 : 2,
          fieldMask: ['QuoteStatus'],
        }));
        const req = NewUpdateWithTokenRequest.create({
          token: TokenProposal.create({ value: token }),
          jobId: proposal.event?.id ?? 0,
          quoteDocumentId: proposal.quoteDocument?.id ?? 0,
          signature: { data: signatureData },
          notify: true,
          quoteLines: updatedQuoteLines,
        });
        const response = await updateWithToken(req);
        if (response?.downloadUrl) setDownloadUrl(response.downloadUrl);
        setIsComplete(true);
      } catch (err) {
        console.error('Error updating proposal:', err);
        setError('Failed to save proposal. Please try again.');
      }
    },
    [proposal, token, isSubmitting, updateWithToken],
  );
  if (loading || !proposal) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500" />
          <p className="mt-4 text-gray-600">Loading proposal...</p>
        </div>
      </div>
    );
  }
  if (isComplete || isProposalSigned) {
    return <CustomerProposalComplete logoUrl={kalosLogo.data} downloadUrl={downloadUrl} />;
  }
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
          <p className="text-red-800">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }
  const companyInfo = {
    name: 'Kalos Services',
    address: '236 Hatteras Ave, Clermont, FL 34711',
    phone: '(352) 243-7088',
    email: 'info@kalosflorida.com',
    logo: kalosLogo.data,
  };
  let validUntil = new Date();
  let agreementDuration = new Date();
  if (proposal.quoteDocument?.quoteExpireDate) {
    validUntil = new Date(proposal.quoteDocument.quoteExpireDate);
  } else {
    validUntil.setDate(validUntil.getDate() + 30);
  }
  if (proposal.quoteDocument?.validUntilDate) {
    agreementDuration = new Date(proposal.quoteDocument.validUntilDate);
  } else {
    agreementDuration.setFullYear(agreementDuration.getFullYear() + 1);
  }
  const quoteLines = proposal.quoteLines || [];
  const quotePhotos = quoteLines.flatMap((ql: QuoteLine) =>
    Array.isArray(ql.photos) ? ql.photos : [],
  );
  return (
    <CustomerProposal
      customer={{
        firstname: proposal.user?.firstname || '',
        lastname: proposal.user?.lastname || '',
        businessname: proposal.user?.businessname || '',
        email: proposal.user?.email || '',
        phone: proposal.user?.phone || '',
      }}
      preparedBy={{
        name: `${proposal.preparedBy?.firstname || ''} ${proposal.preparedBy?.lastname || ''}`.trim(),
        email: proposal.preparedBy?.email || '',
        phone: proposal.preparedBy?.phone || '',
      }}
      companyInfo={companyInfo}
      property={{
        address: proposal.property?.address || '',
        city: proposal.property?.city || '',
        state: proposal.property?.state || '',
        zip: proposal.property?.zip || '',
      }}
      services={quoteLines}
      technicalNotes={proposal.quoteDocument?.jobNotes || ''}
      quoteDocument={proposal.quoteDocument}
      quotePhotos={quotePhotos}
      evidencePhotos={evidencePhotos}
      proposalValidity={{ validUntil, agreementDuration }}
      onComplete={handleComplete}
      isSubmitting={isSubmitting}
      discount={proposal.quoteDocument?.discount}
      discountCost={proposal.quoteDocument?.discountcost}
      taxRate={String(getQuoteDocumentTaxRate(proposal.quoteDocument))}
    />
  );
};

// Helper function to convert base64 string to Blob
const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  const base64Data = b64Data.split(',')[1] || b64Data;
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

export const CustomerProposalWrapper: FC<CustomerProposalWrapperProps> = ({
  quoteDocumentId,
  reload,
}) => {
  const kalosLogo = useKalosLogoQuery();
  const [proposal, setProposal] = useState<Proposal | undefined>();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [evidencePhotos, setEvidencePhotos] = useState<EvidencePhoto[]>([]);
  useEffect(() => {
    let cancelled = false;
    resolveEvidencePhotos(proposal?.photos ?? []).then((items) => {
      if (!cancelled) setEvidencePhotos(items);
    });
    return () => {
      cancelled = true;
    };
  }, [proposal?.photos]);

  // Fetch the quote document by id (to get eventId for newGet and validate it exists)
  const { data: quoteDocListData, isLoading: isQuoteDocLoading } = useQuoteDocumentQuery({
    filters: { id: quoteDocumentId, withoutLimit: true },
    enabled: !!quoteDocumentId,
  });
  const quoteDoc = useMemo(
    () => quoteDocListData?.results?.find((d) => d.id === quoteDocumentId) ?? null,
    [quoteDocListData?.results, quoteDocumentId],
  );

  useEffect(() => {
    const fetchProposal = async () => {
      if (!quoteDoc?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const req: NewGetRequest = { jobId: quoteDoc.eventId, quoteDocumentId: quoteDoc.id };
      const result = await ProposalClientService.newGet(req);
      setProposal(result);
      setLoading(false);
    };
    fetchProposal();
  }, [quoteDoc?.id, quoteDoc?.eventId]);

  // Check if proposal is already signed
  const isProposalSigned = useMemo(() => {
    if (!proposal?.quoteDocument?.signedDate) return false;
    return (
      proposal.quoteDocument.signedDate !== NULL_TIME &&
      proposal.quoteDocument.signedDate !== '0000-00-00 00:00:00' &&
      proposal.quoteDocument.signedDate !== ''
    );
  }, [proposal?.quoteDocument?.signedDate]);

  // Generate approved proposal download URL for signed proposals
  const generateApprovedProposalUrl = useCallback(async () => {
    if (!proposal?.user?.id || (!proposal?.event?.id && !proposal?.quoteDocument?.id)) return '';

    const tryKey = async (key: string) => {
      try {
        const urlObj = URLObject.create({ bucket: 'testbuckethelios', key });
        const res = await S3ClientService.GetDownloadURL(urlObj);
        return res?.url || '';
      } catch (error) {
        console.error('Error generating approved proposal URL:', error);
        return '';
      }
    };

    let url = '';
    if (proposal.quoteDocument?.id) {
      url = await tryKey(`${proposal.quoteDocument.id}_approved_proposal_${proposal.user.id}.pdf`);
    }
    if (!url && proposal.event?.id) {
      url = await tryKey(`${proposal.event.id}_approved_proposal_${proposal.user.id}.pdf`);
    }
    return url;
  }, [proposal?.event?.id, proposal?.quoteDocument?.id, proposal?.user?.id]);

  useEffect(() => {
    if (isProposalSigned) {
      generateApprovedProposalUrl().then((url) => {
        setDownloadUrl(url);
      });
    }
  }, [isProposalSigned, generateApprovedProposalUrl]);

  const handleComplete = useCallback(
    async (data: {
      acceptedServices: number[];
      signature: string;
      proposedBy: string;
      acceptedBy: string;
      signatureDate: string;
    }) => {
      if (!proposal || isSubmitting) {
        return;
      }

      try {
        setIsSubmitting(true);
        setError(null);

        let signatureBlob: { data: Uint8Array } | undefined;
        if (data.signature) {
          const signature = b64toBlob(data.signature, 'image/png');
          const signatureArrayBuffer = await signature.arrayBuffer();
          signatureBlob = { data: new Uint8Array(signatureArrayBuffer) };
        }

        const updatedQuoteLines = proposal.quoteLines.map((quoteLine: QuoteLine) => ({
          ...quoteLine,
          quoteStatus: data.acceptedServices.includes(quoteLine.id) ? 1 : 2,
          fieldMask: ['QuoteStatus'],
        }));

        const updateRequest: NewUpdateWithoutTokenRequest = {
          jobId: proposal.event?.id ?? 0,
          quoteDocumentId: proposal.quoteDocument?.id ?? quoteDocumentId,
          signature: signatureBlob,
          notify: true,
          quoteLines: updatedQuoteLines,
          overrideNote: '',
        };

        const response = await ProposalClientService.newUpdateWithoutToken(updateRequest);

        if (response) {
          const jobId = proposal.event?.id ?? 0;
          const acceptedForDescription = proposal.quoteLines.filter((ql) =>
            data.acceptedServices.includes(ql.id),
          );
          try {
            await persistServiceNeededFromSignedQuoteLines({
              acceptedLines: acceptedForDescription,
              proposalResponse: response,
              fallbackEventId: jobId,
            });
          } catch (persistErr) {
            console.error(
              'Could not update Services Needed from signed proposal lines',
              persistErr,
            );
          }
          const pdfUrl = response.downloadUrl;
          setDownloadUrl(pdfUrl ?? '');
          setIsComplete(true);
        }
      } catch (err) {
        console.error('Error updating proposal:', err);
        setError('Failed to update proposal. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [proposal, quoteDocumentId, isSubmitting],
  );

  const isLoadingPage = isQuoteDocLoading || (!!quoteDoc && loading);
  if (isLoadingPage || !proposal) {
    if (!isQuoteDocLoading && !quoteDoc && quoteDocListData !== undefined) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <p className="mt-4 text-gray-600">Proposal not found.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500" />
          <p className="mt-4 text-gray-600">Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (isComplete || isProposalSigned) {
    return <CustomerProposalComplete logoUrl={kalosLogo.data} downloadUrl={downloadUrl} />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-800">{error}</div>
        <button
          onClick={() => setError(null)}
          className="mt-2 text-sm text-red-600 hover:text-red-800"
        >
          Dismiss
        </button>
      </div>
    );
  }

  const companyInfo = {
    name: 'Kalos Services',
    address: '236 Hatteras Ave, Clermont, FL 34711',
    phone: '(352) 243-7088',
    email: 'info@kalosflorida.com',
    logo: kalosLogo.data,
  };

  let validUntil = new Date();
  let agreementDuration = new Date();
  if (proposal.quoteDocument?.quoteExpireDate) {
    validUntil = new Date(proposal.quoteDocument.quoteExpireDate);
  } else {
    validUntil.setDate(validUntil.getDate() + 30);
  }
  if (proposal.quoteDocument?.validUntilDate) {
    agreementDuration = new Date(proposal.quoteDocument.validUntilDate);
  } else {
    agreementDuration.setFullYear(agreementDuration.getFullYear() + 1);
  }

  const technicalNotes = proposal.quoteDocument?.jobNotes || '';
  const quoteLines = proposal.quoteLines || [];
  const quotePhotos = quoteLines.flatMap((ql: any) => (Array.isArray(ql.photos) ? ql.photos : []));

  return (
    <CustomerProposal
      customer={{
        firstname: proposal.user?.firstname || '',
        lastname: proposal.user?.lastname || '',
        businessname: proposal.user?.businessname || '',
        email: proposal.user?.email || '',
        phone: proposal.user?.phone || '',
      }}
      preparedBy={{
        name: `${proposal.preparedBy?.firstname || ''} ${proposal.preparedBy?.lastname || ''}`.trim(),
        email: proposal.preparedBy?.email || '',
        phone: proposal.preparedBy?.phone || '',
      }}
      companyInfo={companyInfo}
      property={{
        address: proposal.property?.address || '',
        city: proposal.property?.city || '',
        state: proposal.property?.state || '',
        zip: proposal.property?.zip || '',
      }}
      services={quoteLines}
      technicalNotes={technicalNotes}
      quoteDocument={proposal.quoteDocument}
      quotePhotos={quotePhotos}
      evidencePhotos={evidencePhotos}
      proposalValidity={{ validUntil, agreementDuration }}
      onComplete={handleComplete}
      isSubmitting={isSubmitting}
      discount={proposal.quoteDocument?.discount}
      discountCost={proposal.quoteDocument?.discountcost}
      taxRate={String(getQuoteDocumentTaxRate(proposal.quoteDocument))}
    />
  );
};
